import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-[72px] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              ← Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    for (const line of lines) {
      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key} className="text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.trim() === '') {
        elements.push(<div key={key} className="h-4"></div>);
      } else if (line.startsWith('|')) {
        elements.push(
          <div key={key} className="my-6 overflow-x-auto">
            <pre className="text-sm text-foreground-600 bg-background-100 rounded-lg p-4 overflow-x-auto font-mono">
              {line}
            </pre>
          </div>
        );
      } else {
        let html = line;
        html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground-950">$1</strong>');
        html = html.replace(/&ldquo;/g, '\u201C');
        html = html.replace(/&rdquo;/g, '\u201D');
        elements.push(
          <p
            key={key}
            className="text-base text-foreground-600 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: html }}
          ></p>
        );
      }
      key++;
    }

    return elements;
  };

  const otherArticles = articles.filter((a) => a.slug !== slug);

  return (
    <>
      <Navbar />
      <main>
        <article className="min-h-screen pt-[72px]">
          <div className="px-4 md:px-6 py-12 bg-background-50 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-500">Guides</span>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700">{article.title}</span>
              </nav>

              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-800 rounded-full mb-4">
                {article.category}
              </span>

              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
                {article.h1}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-400">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                  <i className="ri-team-line text-sm"></i>
                </div>
                <div>
                  <p className="font-medium text-foreground-700">Kakobuy Finds Research Team</p>
                  <p>Updated 2026 · 8 min read</p>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 py-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="w-full lg:w-2/3">
                  <div className="prose-custom">
                    {renderContent(article.content)}
                  </div>

                  <div className="mt-12 pt-8 border-t border-background-200/70">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>
                      Back to Kakobuy Finds Homepage
                    </Link>
                  </div>
                </div>

                <aside className="w-full lg:w-1/3">
                  <div className="lg:sticky lg:top-[88px] space-y-6">
                    <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">Quick Links</h4>
                      <div className="space-y-3">
                        <Link
                          to="/"
                          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          <i className="ri-home-4-line"></i>
                          Back to Homepage
                        </Link>
                        <a
                          href="https://kaspreadsheet.com"
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600"
                        >
                          <i className="ri-external-link-line"></i>
                          Explore Spreadsheet
                        </a>
                      </div>
                    </div>

                    <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">Related Categories</h4>
                      <div className="flex flex-wrap gap-2">
                        <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="px-3 py-1.5 text-xs font-medium bg-background-50 text-foreground-600 rounded-full border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-colors">Shoes</a>
                        <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="px-3 py-1.5 text-xs font-medium bg-background-50 text-foreground-600 rounded-full border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-colors">Hoodies</a>
                        <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="px-3 py-1.5 text-xs font-medium bg-background-50 text-foreground-600 rounded-full border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-colors">T-Shirts</a>
                        <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="px-3 py-1.5 text-xs font-medium bg-background-50 text-foreground-600 rounded-full border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-colors">Jackets</a>
                        <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="px-3 py-1.5 text-xs font-medium bg-background-50 text-foreground-600 rounded-full border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-colors">Accessories</a>
                        <a href="https://kaspreadsheet.com/search-921/name/Shoes.html" target="_blank" rel="noopener nofollow" className="px-3 py-1.5 text-xs font-medium bg-background-50 text-foreground-600 rounded-full border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-colors">Bags</a>
                      </div>
                    </div>

                    {article.relatedLinks.length > 0 && (
                      <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                        <h4 className="text-sm font-semibold text-foreground-900 mb-4">Related Resources</h4>
                        <div className="space-y-3">
                          {article.relatedLinks.map((link, idx) => (
                            <div key={idx}>
                              {link.url.startsWith('http') ? (
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener nofollow"
                                  className="text-sm text-foreground-600 hover:text-primary-600 transition-colors block"
                                >
                                  {link.text} →
                                </a>
                              ) : (
                                <Link
                                  to={link.url}
                                  className="text-sm text-foreground-600 hover:text-primary-600 transition-colors block"
                                >
                                  {link.text} →
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </article>

        {otherArticles.length > 0 && (
          <section className="w-full py-16 bg-background-100">
            <div className="px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-2xl font-heading font-bold text-foreground-950 mb-8">More Guides</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {otherArticles.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/${a.slug}`}
                      className="group flex gap-4 bg-background-50 rounded-lg border border-background-200/70 p-4 hover:border-primary-200/50 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={a.imageUrl}
                          alt={a.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-accent-700">{a.category}</span>
                        <h4 className="text-sm font-semibold text-foreground-900 mt-1 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                          {a.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{a.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.h1,
            description: article.metaDescription,
            image: article.imageUrl,
            author: {
              '@type': 'Organization',
              name: 'Kakobuy Finds Research Team',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Kakobuy Finds',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
              },
            },
            datePublished: '2026-01-15',
            dateModified: '2026-07-09',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://kakobuyfinds.bond/${article.slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://kakobuyfinds.bond/' },
              { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://kakobuyfinds.bond/kakobuy-spreadsheet-guide' },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://kakobuyfinds.bond/${article.slug}` },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: (() => {
              const sections = article.content.split('\n').filter((l: string) => l.startsWith('## '));
              return sections.map((heading: string, idx: number) => {
                const question = heading.replace('## ', '').trim();
                const contentLines = article.content.split('\n');
                const headingIdx = contentLines.findIndex((l: string) => l === heading);
                let answerLines: string[] = [];
                if (headingIdx >= 0) {
                  for (let i = headingIdx + 1; i < contentLines.length; i++) {
                    if (contentLines[i].startsWith('## ')) break;
                    const trimmed = contentLines[i].trim();
                    if (trimmed && !trimmed.startsWith('|')) answerLines.push(trimmed);
                  }
                }
                const answerText = answerLines.join(' ').substring(0, 300);
                return {
                  '@type': 'Question',
                  name: question + '?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: answerText || 'Learn more about ' + question.toLowerCase() + ' in our comprehensive Kakobuy spreadsheet guide.',
                  },
                };
              });
            })(),
          }),
        }}
      />
    </>
  );
}